import { createRouter, createWebHistory } from 'vue-router'
import type { RouteRecordRaw } from 'vue-router'
import { useAuthStore } from '@/stores/auth'

// 导入路由配置
import authRoutes from './auth'
import platformRoutes from './platform'
import merchantRoutes from './merchant'
import profileRoutes from './profile'
import { USER_TYPES, isBranchUserType } from '@/constants/userRoles'

const routes: RouteRecordRaw[] = [
  {
    path: '/',
    redirect: '/platform/merchants',
  },
  {
    path: '/dashboard',
    redirect: '/platform/merchants',
  },
  // 认证路由
  ...authRoutes,
  // 平台管理路由
  ...platformRoutes,
  // 组织管理路由
  ...merchantRoutes,
  // 个人中心
  ...profileRoutes,
  {
    path: '/404',
    name: 'NotFound',
    component: () => import('@/views/common/NotFound.vue'),
    meta: { layout: 'blank' },
  },
  {
    path: '/:pathMatch(.*)*',
    redirect: '/404',
  },
]

const router = createRouter({
  history: createWebHistory(),
  routes,
})

// 路由守卫
router.beforeEach(async (to, _from, next) => {
  const authStore = useAuthStore()
  const isBranchBusiness =
    /^\/merchant\/(members|children|employees|shops|points\/records|rules\/(points|discount))\/?$/.test(
      to.path
    )
  if (isBranchBusiness && authStore.isAuthenticated) {
    try {
      await authStore.refreshProfile()
      if (isBranchUserType(authStore.userType) && !authStore.user?.shopIds?.length) {
        next('/unauthorized')
        return
      }
    } catch {
      next(authStore.isAuthenticated ? '/unauthorized' : '/login')
      return
    }
  }
  const resolveViewModules = () => {
    const permissionCodes = authStore.user?.permissionCodes
    if (Array.isArray(permissionCodes)) {
      return permissionCodes.filter((code) => code.startsWith('module_'))
    }
    return authStore.user?.enabledModuleIds || []
  }
  const resolveHomePath = () => {
    if (authStore.user?.userType === USER_TYPES.PLATFORM_ADMIN) {
      return '/platform/merchants'
    }
    const modules = resolveViewModules()
    const moduleHomeMap: Array<[string, string]> = [
      ['module_rules', '/merchant/rules/points'],
      ['module_member', '/merchant/members'],
      ['module_points', '/merchant/points/records'],
      ['module_employee', '/merchant/employees'],
      ['module_shop', '/merchant/shops'],
      ['module_mall', '/merchant/mall/products'],
      ['module_child', '/merchant/children'],
      ['module_service_appointment', '/merchant/service-appointments'],
      ['module_outstanding_student', '/merchant/outstanding-students'],
      ['module_news', '/merchant/news'],
      ['module_recommended_courses', '/merchant/recommended-courses'],
    ]
    return (
      moduleHomeMap.find(([moduleCode]) => modules.includes(moduleCode))?.[1] || '/unauthorized'
    )
  }

  // 如果已登录且访问登录页，跳转到首页
  if (to.name === 'Login' && authStore.isAuthenticated) {
    next(resolveHomePath())
    return
  }

  // 未登录访问受保护页面时，必须先进入登录页，避免后续权限重定向误判
  if (to.meta.requiresAuth && !authStore.isAuthenticated) {
    next('/login')
    return
  }

  const requestedRootPaths = ['/', '/dashboard']
  if (requestedRootPaths.includes(to.path)) {
    if (!authStore.isAuthenticated) {
      next('/login')
      return
    }
    const target = resolveHomePath()
    if (to.path !== target) {
      next(target)
      return
    }
  }

  if (
    authStore.isAuthenticated &&
    authStore.user?.userType !== USER_TYPES.PLATFORM_ADMIN &&
    to.path.startsWith('/platform')
  ) {
    const target = resolveHomePath()
    if (to.path !== target) {
      next(target)
      return
    }
  }

  // 检查权限
  const requiredPermissions = to.meta.requiredPermissions as string[]
  if (requiredPermissions && requiredPermissions.length > 0) {
    if (!authStore.hasPermissions(requiredPermissions)) {
      next('/unauthorized')
      return
    }
  }

  const requiredModules = to.meta.requiredModules as string[] | undefined
  if (requiredModules && requiredModules.length > 0) {
    const enabledModules = resolveViewModules()
    const missingModule = requiredModules.some((code) => !enabledModules.includes(code))
    if (missingModule) {
      next('/unauthorized')
      return
    }
  }

  next()
})

// 全局后置守卫
router.afterEach((to) => {
  // 设置页面标题
  const title = to.meta.title as string
  if (title) {
    document.title = `${title} - 会员管理系统`
  } else {
    document.title = '会员管理系统'
  }
})

export default router
